/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { useToggle, useTranslate } from "@adapptio/lib-components";
import React, { ChangeEvent, FC, useCallback, useEffect, useRef } from "react";
import { IFieldValidationError } from "./FieldTypes";
import { FieldWrapper } from "./FieldWrapper";
import { useForkRef } from "../../hooks/useForkRef";
import { Input } from "../Input";
import { InputAdornment } from "../InputAdornment";
import { FieldValidationErrors } from "./FieldValidationErrors";
import { InputButton } from "../InputButton";
import { Icon, ICON_SIZE } from "../Icon";

export interface IFieldTextProps {
	id?: string;
	label?: string;
	value: string;
	placeholder?: string;
	readOnly?: boolean;
	validationErrors?: IFieldValidationError[];
	className?: string;
	onChange?: (value: string, ev: ChangeEvent<HTMLInputElement>) => void;
	onPressEnter?: (value: string, ev: React.KeyboardEvent<HTMLInputElement>) => void;
	onBlur?: React.FocusEventHandler<HTMLInputElement>;
	minLength?: number;
	maxLength?: number;
	grow?: boolean;
	inputRef?: React.MutableRefObject<HTMLInputElement>;
	autoFocus?: boolean;
	autoFocusSelect?: boolean;
	type?: React.HTMLProps<HTMLInputElement>["type"];
	debounceTimeout?: number;
	multiLine?: boolean;
	maxRows?: number;
}

export const FieldText: FC<IFieldTextProps> = ({
	id,
	value,
	placeholder,
	readOnly,
	validationErrors,
	className,
	grow,
	onBlur,
	onChange,
	onPressEnter,
	inputRef,
	minLength,
	maxLength,
	autoFocus,
	autoFocusSelect,
	type,
	debounceTimeout,
	multiLine,
	maxRows
}) => {
	const elRef = useRef<HTMLInputElement>();
	const forkRef = useForkRef(elRef, inputRef);
	const t = useTranslate();
	const [ passwordVisible, togglePasswordVisibility ] = useToggle(false);
	const isPasswordField = type === "password";

	const displayedType = isPasswordField && passwordVisible ? "text" : type;

	const handleChange = useCallback(
		(ev: ChangeEvent<HTMLInputElement>) => {
			if (onChange) {
				onChange(ev.target.value, ev);
			}
		},
		[ onChange ]
	);

	const handleKeyUp = useCallback(
		(ev: React.KeyboardEvent<HTMLInputElement>) => {
			if (ev.key === "Enter" && onPressEnter) {
				onPressEnter(ev.currentTarget.value, ev);
			}
		},
		[ onChange ]
	);

	useEffect(() => {
		if (autoFocus) {
			elRef.current?.focus();
		}
	}, [ autoFocus ]);

	return (
		<FieldWrapper type="text" className={className} grow={grow}>
			<Input
				id={id}
				className="edt-field-control"
				type={displayedType}
				value={value || ""}
				placeholder={placeholder ? t("editor", placeholder) : undefined}
				onChange={handleChange}
				onBlur={onBlur}
				ref={forkRef}
				minLength={minLength}
				maxLength={maxLength}
				autoFocus={autoFocus}
				onKeyUp={handleKeyUp}
				autoFocusSelect={autoFocusSelect}
				readOnly={readOnly}
				debounceTimeout={debounceTimeout}
				multiLine={multiLine}
				maxRows={maxRows}
				endAdornment={
					isPasswordField || validationErrors?.length > 0 ? (
						<>
							{isPasswordField && (
								<InputAdornment>
									<InputButton type="button" onClick={togglePasswordVisibility}>
										<Icon
											name={passwordVisible ? "mdi/eye-off" : "mdi/eye"}
											size={ICON_SIZE.XSMALL}
										/>
									</InputButton>
								</InputAdornment>
							)}
							{validationErrors?.length > 0 && (
								<InputAdornment>
									<FieldValidationErrors errors={validationErrors} />
								</InputAdornment>
							)}
						</>
					) : null
				}
			/>
		</FieldWrapper>
	);
};
